import { injectSpeedInsights } from '@vercel/speed-insights';
import { BoothStore } from '../data/BoothStore';
import { RoomChannel, type RoomStatePayload } from '../lib/roomChannel';
import { PresenceChannel, type PresenceUser } from '../lib/presenceChannel';
import { FakePresence, type FakeUserRecord } from '../lib/fakePresence';
import { ActivityBar } from '../room/ActivityBar';
import { prefetchAvatarByDefId } from '../lib/avatarPrefetch';

// Landing — homepage for BoothFM. Single canonical primary booth.
//
// The page is a quiet broadcast console: hero, live-now module, one-line
// about, footer. The live-now module reflects the actual state of the
// primary booth — current track, DJ, listener count, set elapsed time —
// via the same RoomChannel and PresenceChannel the room itself uses. No
// fake metrics, no placeholder DJs; if nothing is playing we say so.
//
// The booth's slug + name are read live from `public.booths` so renames
// (e.g. "Main Room" → "Main Booth") propagate to the hero CTA without a
// code change. With multiple booths in the future, [0] needs to be
// replaced by an explicit primary flag — for now `BoothStore.list()`
// returns one row.

interface LandingDOM {
  trackTitle: HTMLElement;
  trackBy: HTMLElement;
  listeners: HTMLElement;
  elapsed: HTMLElement;
  clock: HTMLElement;
  presence: HTMLElement;
  activityBar: HTMLElement;
  heroCta: HTMLAnchorElement;
  liveRoom: HTMLElement;
  liveEnter: HTMLAnchorElement;
}

function dom(): LandingDOM | null {
  const trackTitle  = document.getElementById('live-track-title');
  const trackBy     = document.getElementById('live-track-by');
  const listeners   = document.getElementById('live-listeners');
  const elapsed     = document.getElementById('live-elapsed');
  const clock       = document.getElementById('live-now-clock');
  const presence    = document.getElementById('live-presence');
  const activityBar = document.getElementById('activity-bar');
  const heroCta     = document.getElementById('hero-cta') as HTMLAnchorElement | null;
  const liveRoom    = document.getElementById('live-room');
  const liveEnter   = document.getElementById('live-enter') as HTMLAnchorElement | null;
  if (!trackTitle || !trackBy || !listeners || !elapsed || !clock || !presence || !activityBar || !heroCta || !liveRoom || !liveEnter) {
    return null;
  }
  return { trackTitle, trackBy, listeners, elapsed, clock, presence, activityBar, heroCta, liveRoom, liveEnter };
}

function roomUrl(slug: string): string {
  const host = window.location.host;
  if (host && !host.startsWith('localhost') && !host.startsWith('127.0.0.1')) {
    return `/room/${slug}`;
  }
  return `/room.html?room=${slug}`;
}

function formatElapsed(startedAt: number): string {
  const ms = Math.max(0, Date.now() - startedAt);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatClock(d = new Date()): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function initials(handle: string): string {
  const parts = handle.trim().split(/[\s_\-.]+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;'
    : c === '<' ? '&lt;'
    : c === '>' ? '&gt;'
    : c === '"' ? '&quot;'
    : '&#39;',
  );
}

class LandingController {
  private d: LandingDOM;
  private activity: ActivityBar;
  private startedAt: number | null = null;
  private elapsedTimer: number | null = null;
  private clockTimer: number | null = null;
  // House DJ status from the latest room state; combined with presence
  // to compute the total listener count. The house DJ is "virtually
  // present" — not in the presence channel, but reflected in counts so
  // the homepage never reads as a dead venue.
  private houseDjSpinning = false;
  private houseDjHandle: string | null = null;
  private lastRemote: PresenceUser[] = [];
  // Ambient fake listeners. From the visitor's perspective these are
  // real participants — they show up in both the count and the avatar
  // strip alongside whatever real humans are connected. Source of truth
  // is public.fake_room_presence, reconciled by the reconciler RPC.
  private lastFakes: FakeUserRecord[] = [];
  private roomName = 'the booth';
  /** Avatar3dId of the currently-spinning DJ. Used to gate the avatar
   *  prefetch so a state tick that doesn't change the avatar doesn't
   *  re-inject the same <link rel="prefetch"> tag. */
  private lastPrefetchedAvatarId: string | null = null;

  constructor(d: LandingDOM) {
    this.d = d;
    this.activity = new ActivityBar(d.activityBar);
    this.activity.setSlotAvailable(true);
    this.d.clock.textContent = formatClock();
    this.clockTimer = window.setInterval(() => {
      this.d.clock.textContent = formatClock();
    }, 30_000);
  }

  setRoomName(name: string): void {
    this.roomName = name;
    this.activity.setRoom(name);
    this.d.liveRoom.textContent = name;
  }

  setIdle(reason: 'no-room' | 'between-tracks'): void {
    this.startedAt = null;
    this.stopElapsedTicker();
    this.d.trackTitle.textContent =
      reason === 'no-room' ? 'Doors closed' : 'Between tracks';
    this.d.trackBy.textContent =
      reason === 'no-room'
        ? `${this.roomName} hasn’t opened yet.`
        : 'A new set is about to start.';
    this.d.elapsed.textContent = '—';
    this.activity.setNowPlaying(null, null);
    this.activity.setSlotAvailable(true);
  }

  applyRoomState(state: RoomStatePayload): void {
    const ns = state.nowSpinning;
    this.houseDjSpinning = ns?.isHouseDj === true;
    this.houseDjHandle = this.houseDjSpinning ? (ns?.djHandle?.trim() || null) : null;
    // Warm the HTTP cache for the spinning DJ's avatar so navigation
    // into the room finds the GLB already on disk. Fire-and-forget;
    // gated on lastPrefetchedAvatarId so a state tick that doesn't
    // change the avatar isn't wasted work. The prefetch helper itself
    // is also idempotent across the whole page session.
    const avatarId = ns?.avatar3dId ?? null;
    if (avatarId && avatarId !== this.lastPrefetchedAvatarId) {
      this.lastPrefetchedAvatarId = avatarId;
      void prefetchAvatarByDefId(avatarId);
    }
    if (!ns) {
      this.setIdle('between-tracks');
      this.renderListeners();
      return;
    }
    this.d.trackTitle.textContent = ns.track.title;
    const artist = ns.track.artist?.trim();
    const djHandle = ns.djHandle?.trim() || 'unknown';
    this.d.trackBy.innerHTML = artist
      ? `${escapeHtml(artist)} <span class="live-track-sep" aria-hidden="true">—</span> spun by <span class="live-track-dj">@${escapeHtml(djHandle)}</span>`
      : `Spun by <span class="live-track-dj">@${escapeHtml(djHandle)}</span>`;
    this.startedAt = ns.startedAt;
    this.tickElapsed();
    this.startElapsedTicker();

    const tickerTrack = artist ? `${ns.track.title} — ${artist}` : ns.track.title;
    this.activity.setSlotAvailable(false);
    this.activity.setNowPlaying(`@${djHandle}`, tickerTrack);
    this.renderListeners();
  }

  applyPresence(remote: PresenceUser[]): void {
    // Landing-side viewer is a spectator that hasn't joined the floor,
    // so they are not in the presence roster. To approximate the public
    // "listeners" number we report the size of the remote set; the room
    // itself accounts for the local body once a user enters.
    this.lastRemote = remote;
    this.renderListeners();
  }

  applyFakePresence(fakes: FakeUserRecord[]): void {
    this.lastFakes = fakes;
    this.renderListeners();
  }

  // Combined render: real humans from presence + the ambient fake
  // roster + (1 if the house DJ is the current spinner). From the
  // visitor's perspective these are all "listeners"; the homepage never
  // distinguishes real vs fake in either the count or the strip.
  // Kept as a single method so applyRoomState / applyPresence /
  // applyFakePresence stay write-only — they all call this.
  private renderListeners(): void {
    const remote = this.lastRemote;
    const fakes = this.lastFakes;
    const houseDjCount = this.houseDjSpinning ? 1 : 0;
    const count = remote.length + fakes.length + houseDjCount;
    this.d.listeners.textContent = count === 0 ? '0' : String(count);

    if (count === 0) {
      this.d.presence.innerHTML = `<span class="live-presence-empty">Doors open. Quiet so far.</span>`;
      return;
    }

    // Only the house DJ is "present" — no real humans, no ambient fakes
    // yet (typically the first few seconds before the reconciler ticks).
    if (remote.length === 0 && fakes.length === 0 && houseDjCount > 0) {
      const handle = this.houseDjHandle ?? 'house';
      this.d.presence.innerHTML = `
        <span class="live-presence-avatars"><span class="live-presence-avatar" title="${escapeHtml(handle)}">${escapeHtml(initials(handle))}</span></span>
        <span class="live-presence-handles">@${escapeHtml(handle)} holding it down — be the first to join.</span>
      `;
      return;
    }

    // Merge real + fake into a single listener list. Real users are
    // ordered first so the strip leads with humans when there are any —
    // a small subliminal signal that the room has real life in it. The
    // strip itself uses identical markup for both, in line with the
    // brief: never visually label a fake user as fake.
    interface Listener { handle: string; }
    const listeners: Listener[] = [
      ...remote.map((u) => ({ handle: u.handle })),
      ...fakes.map((f) => ({ handle: f.alias })),
    ];

    const shown = listeners.slice(0, 5);
    const handles = shown.map((u) => u.handle);
    const extra = listeners.length - shown.length;
    const handleLine = extra > 0
      ? `${handles.join(', ')} +${extra}`
      : handles.join(', ');

    const avatars = shown
      .map((u) => `<span class="live-presence-avatar" title="${escapeHtml(u.handle)}">${escapeHtml(initials(u.handle))}</span>`)
      .join('');

    this.d.presence.innerHTML = `
      <span class="live-presence-avatars">${avatars}</span>
      <span class="live-presence-handles">${escapeHtml(handleLine)} — currently listening</span>
    `;
  }

  private startElapsedTicker(): void {
    this.stopElapsedTicker();
    this.elapsedTimer = window.setInterval(() => this.tickElapsed(), 1000);
  }

  private stopElapsedTicker(): void {
    if (this.elapsedTimer !== null) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private tickElapsed(): void {
    if (this.startedAt === null) {
      this.d.elapsed.textContent = '—';
      return;
    }
    this.d.elapsed.textContent = formatElapsed(this.startedAt);
  }

  destroy(): void {
    this.stopElapsedTicker();
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }
}

async function wireLive(controller: LandingController, heroCta: HTMLAnchorElement): Promise<void> {
  // Dynamic primary-booth lookup. Avoids the brittle hardcoded-slug
  // pattern that broke the hero link on rename.
  const booths = await BoothStore.list();
  const booth = booths[0] ?? null;
  if (!booth) {
    controller.setIdle('no-room');
    return;
  }

  heroCta.href = roomUrl(booth.slug);
  heroCta.textContent = `Enter ${booth.name}`;
  // "Enter Room" footer link inside the live-now module also points at
  // the room — keep it in sync with the dynamic slug.
  const refs = dom();
  if (refs) {
    refs.liveEnter.href = roomUrl(booth.slug);
  }
  controller.setRoomName(booth.name);

  const roomChannel = new RoomChannel(booth.id);
  const initial = await roomChannel.start();
  if (initial) controller.applyRoomState(initial);
  else controller.setIdle('between-tracks');
  roomChannel.onState((state) => controller.applyRoomState(state));

  const presence = new PresenceChannel(booth.id);
  presence.onSync((users) => controller.applyPresence(users));
  presence.start();

  // Ambient fake listeners — same room id used by the room page (see
  // FAKE_ROOM_ID in src/room/main.ts). We don't tick the reconciler
  // from the homepage; the room page's open tabs and any cron wiring
  // own that. The homepage just reads what's already there so the
  // listener strip mirrors what a visitor would see if they stepped in.
  const fakePresence = new FakePresence('main');
  fakePresence.onSync((fakes) => controller.applyFakePresence(fakes));
  void fakePresence.start();
}

function boot(): void {
  // Real-user perf monitoring (Core Web Vitals — LCP, INP, CLS, FCP, TTFB).
  // Auto-no-op on localhost; reports to the Vercel Speed Insights dashboard
  // for the linked project on prod deploys.
  injectSpeedInsights({ route: '/' });

  const refs = dom();
  if (!refs) return;
  const controller = new LandingController(refs);
  void wireLive(controller, refs.heroCta);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
